import { faImage } from "@fortawesome/pro-solid-svg-icons";
import { SxProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";

import { Icon } from "../Icon/Icon";

interface ImageGridProps {
  imageSrcs: string[];
  rows: number;
  columns: number;
  className?: string;
  minimizeTiles?: boolean;
  sx?: SxProps;
  slotProps?: {
    placeholder?: Record<string, any>;
  };
}

const containerProps: PropertyKey[] = ["rows", "columns", "aspectRatio"];

const Container = styled("span", {
  shouldForwardProp: (prop) => !containerProps.includes(prop),
})<{ rows: number; columns: number; aspectRatio: string }>(
  ({ theme, rows, columns, aspectRatio }) => ({
    display: rows === 1 && columns === 1 ? "flex" : "grid",
    height: "100%",
    aspectRatio,
    gridTemplateRows:
      rows === 1 ? undefined : `repeat(${rows}, minmax(0, 1fr))`,
    gridTemplateColumns:
      columns === 1 ? undefined : `repeat(${columns}, minmax(0, 1fr))`,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.secondary[7],
  }),
);

const Placeholder = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.secondary[3],
}));

const ImageContainer = styled("span")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  aspectRatio: 1,
  width: "100%",
  height: "100%",
  backgroundColor: theme.palette.secondary[7],

  "& img": {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "100%",
    objectFit: "contain",
  },
}));

const getPlaceholderCount = (
  imageSrcs: string[],
  tileCount: number,
  minimizeTiles: boolean,
) => {
  if (!minimizeTiles) {
    return tileCount - imageSrcs.length;
  } else if (imageSrcs.length === 0) {
    return 1;
  } else if (imageSrcs.length === 1) {
    return 0;
  }

  return tileCount - imageSrcs.length;
};

export const ImageGrid = (props: ImageGridProps) => {
  const {
    rows,
    columns,
    imageSrcs,
    className,
    minimizeTiles = true,
    sx,
    slotProps,
  } = props;
  const tileCount = rows * columns;
  const imagesSrcsToShow = imageSrcs.slice(0, tileCount);
  const isMultipleImagesToShow = imagesSrcsToShow.length > 1;
  const shouldSimplifyGrid = !isMultipleImagesToShow && minimizeTiles;
  const finalRowCount = !shouldSimplifyGrid ? rows : 1;
  const finalColumnCount = !shouldSimplifyGrid ? columns : 1;
  const [erroredIndexes, setErroredIndexes] = useState<number[]>([]);

  const placeholderCount = getPlaceholderCount(
    imageSrcs,
    tileCount,
    minimizeTiles,
  );

  const handleError = (index: number) => {
    setErroredIndexes((prev) => [...prev, index]);
  };

  return (
    <Container
      rows={finalRowCount}
      columns={finalColumnCount}
      className={className}
      aspectRatio={`${columns} / ${rows}`}
      sx={sx}
    >
      {imagesSrcsToShow.map((imageSrc, index) =>
        erroredIndexes.includes(index) ? (
          <Placeholder key={`${imageSrc}-${index}`} {...slotProps?.placeholder}>
            <Icon size="large" icon={faImage} />
          </Placeholder>
        ) : (
          <ImageContainer key={`${imageSrc}-${index}`}>
            <img
              alt={`Image ${index + 1}`}
              src={imageSrc}
              loading="lazy"
              onError={() => handleError(index)}
            />
          </ImageContainer>
        ),
      )}
      {Array.from({ length: placeholderCount }).map((_, index) => (
        <Placeholder key={index} {...slotProps?.placeholder}>
          <Icon size="large" icon={faImage} />
        </Placeholder>
      ))}
    </Container>
  );
};
